import { EXPORT_TIMEOUT, STATS_REQUST_TIMEOUT } from '../util/environment';

import { DeviceListResponse } from '../interface/response/device-list/DeviceListResponse';
import { DeviceResponse } from '../interface/response/device/DeviceResponse';
import { DeviceLogsResponse } from '../interface/response/device-logs/DeviceLogsResponse';
import { DeviceLogsRequest } from '../interface/request/DeviceLogsRequest';
import { DeviceStatusResponse } from '../interface/response/device-status/DeviceStatusResponse';
import { ShellVersionsResponse } from '../interface/response/ShellVersionsResponse';
import { UpdateShellRequest } from '../interface/request/UpdateShellRequest';

import { ColumnName } from '../enum/health-monitor/ColumnName';
import { AutocompleteResponse } from '../interface/response/AutocompleteResponse';
import { GeoFiltersRequest } from '../interface/request/GeoFiltersRequest';
import { StatsDataRequest } from '../interface/request/StatsDataRequest';
import { Export } from '../enum/Export';
import { ApplicationConfig, ConfigResponse } from '../interface/response/ApplicationConfig';
import { CashRegisterDataRequest } from '../interface/request/CashRegisterRequest';
import { CashRegisterDataResponse } from '../interface/response/CashRegisterDataResponse';
import { StatsExport } from '../interface/request/StatsExport';
import { CashRegisterDetailsRequest } from '../interface/request/CashRegisterDetailsRequest';
import { CashRegisterDetailsResponse } from '../interface/response/CashRegisterDetailsResponse';
import { OperatorDetailsRequest } from '../interface/request/OperatorDetailsRequest';
import { OperatorDetailsResponse } from '../interface/response/OperatorDetailsResponse';
import { StatsResponse } from '../interface/stats/StatsResponse';
import axiosInstanceHealthMonitor from './axios-health-monitor';
import axiosInstanceAGMS from './axios-agms';
import { CashRegisterPayinPayoutRequest } from '../interface/request/CashRegisterPayinPayoutRequest';
import { CashRegisterPayinPayoutResponse } from '../interface/response/CashRegisterPayinPayoutResponse';

export const ApiConsumer = {
  async getDeviceList(
    page: number = 0,
    size: number = 20,
    desc: boolean = true,
    sortColumn: ColumnName = ColumnName.NAME,
    searchColumn: ColumnName = ColumnName.NAME,
    search: string = '',
    warnings: string = '',
    status: string = '',
  ): Promise<DeviceListResponse> {
    let response;

    response = await axiosInstanceHealthMonitor.post('/devices', {
      page: page,
      size: size,
      descending: desc,
      sortColumn: sortColumn,
      searchColumn: searchColumn,
      search: search,
      warnings: warnings,
      status: status,
    });

    return {
      ...response.data,
      totalWarningsByType: JSON.stringify(response.data.totalWarningsByType),
    };
  },

  async getAutocompleteList(search: string, column: ColumnName, maxSize: number): Promise<AutocompleteResponse> {
    let response;

    response = await axiosInstanceHealthMonitor.post('/autocomplete', {
      search: search,
      column: column,
      maxSize: maxSize,
    });

    return response.data;
  },

  async getDeviceDetails(deviceId: string): Promise<DeviceResponse> {
    let response;

    response = await axiosInstanceHealthMonitor.get(`/deviceInfo/?deviceUserId=${deviceId}`);

    return response.data;
  },

  async getDeviceLogs(deviceLogsRequest: DeviceLogsRequest): Promise<DeviceLogsResponse[]> {
    let response;

    response = await axiosInstanceHealthMonitor.post('/deviceLogs', deviceLogsRequest);

    return response.data;
  },

  async getRemoteAccess(deviceId: string | null): Promise<string> {
    let response;

    response = await axiosInstanceHealthMonitor.get(`/remoteAccess/?deviceId=${deviceId}`);

    return response.data;
  },

  async getDeviceStatus(deviceId: string | undefined): Promise<DeviceStatusResponse> {
    let response;

    response = await axiosInstanceHealthMonitor.get(`/status/?deviceId=${deviceId}`);

    return response.data;
  },

  async getShellVersions(deviceUserId: string): Promise<ShellVersionsResponse[]> {
    let response;

    response = await axiosInstanceHealthMonitor.get(`/shell/versions?deviceUserId=${deviceUserId}`);

    return response.data;
  },

  async getTimezones(): Promise<string[]> {
    let response;
    response = await axiosInstanceAGMS.get(`DropDownList/GetTimezones`);
    return response.data;
  },

  async export(payload: StatsExport): Promise<any> {
    let response;

    response = await axiosInstanceAGMS.post('api/Stats/Export', payload, {
      responseType: Export.BLOB,
      timeout: EXPORT_TIMEOUT,
    });

    const blob = new Blob([response.data], {
      type: Export.EXCEL_TYPE,
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = Export.EXPORT;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  },

  async getStats(payload: StatsDataRequest): Promise<StatsResponse> {
    let response;

    response = await axiosInstanceAGMS.post('/api/Stats', payload, { timeout: STATS_REQUST_TIMEOUT });

    return response.data;
  },

  async getCashRegisterData(payload: CashRegisterDataRequest): Promise<CashRegisterDataResponse> {
    let response;

    response = await axiosInstanceAGMS.post('/api/CashRegister', payload);
    // only for testing
    // for (let i = 0; i < 500; i++) {
    //   const item = {
    //     GroupID: null,
    //     GroupName: 'Group ' + i,
    //     LocationID: 'Loc' + i,
    //     LocationName: 'Location ' + i,
    //     DeviceID: 'Dev' + i,
    //     DeviceName: 'Device ' + i,
    //     StartState: 1000 + i,
    //     Changes: 500 + i,
    //     EndState: 1500 + i,
    //     Currency: 'USD',
    //   };
    //   response.data.Collection.push(item)
    // }
    // response.status = AgmsResponseCode.OK

    return response.data;
  },

  async getCashRegisterDetails(payload: CashRegisterDetailsRequest): Promise<CashRegisterDetailsResponse> {
    let response;

    response = await axiosInstanceAGMS.post('/api/CashRegister/Details', payload);

    return response.data;
  },

  async getOperatorDetails(payload: OperatorDetailsRequest): Promise<OperatorDetailsResponse> {
    let response;

    response = await axiosInstanceAGMS.post('/api/CashRegister/OperatorDetails', payload);

    return response.data;
  },

  async postCashRegisterPayInPayOut(payload: CashRegisterPayinPayoutRequest): Promise<CashRegisterPayinPayoutResponse> {
    let response;

    response = await axiosInstanceAGMS.post('api/CashRegister/PayinPayout', payload);

    return response.data;
  },

  async getGeoFilters(payload: GeoFiltersRequest): Promise<any> {
    let response;

    response = await axiosInstanceAGMS.post('/api/Geo/Filter', payload);

    return response.data;
  },

  async getGamesFilter(payload: {}): Promise<any> {
    let response;

    response = await axiosInstanceAGMS.get('api/Filter/Games', {
      params: payload,
    });

    return response.data;
  },

  async getConfig(): Promise<ConfigResponse> {
    const response = await axiosInstanceAGMS.get('api/Geo/Config');

    return response.data as ConfigResponse;
  },

  async updateConfig(payload: ApplicationConfig): Promise<ConfigResponse> {
    const response = await axiosInstanceAGMS.post('api/Geo/Config', payload);

    return response.data as ConfigResponse;
  },

  async setShellVersions(updateShellRequest: UpdateShellRequest): Promise<any> {
    let response;

    response = await axiosInstanceHealthMonitor.post(`/shell/versions`, updateShellRequest);

    return response.data;
  },

  async restartShell(deviceId: string): Promise<any> {
    let response;

    response = await axiosInstanceHealthMonitor.get(`/restart?deviceUserId=${deviceId}`);

    return response.data;
  },
};
